/**
 * Staff inbox for incoming training-course enquiries.
 *
 * Each row shows the enquirer, contact details, marketing source, and a
 * status pipeline (New → Contacted → Booked → Lost / Archived). Status &
 * notes are saved inline. An "Embed snippet" panel at the bottom gives
 * the user the copy-pasteable iframe code for their training website.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, GraduationCap, Mail, Phone, PhoneCall, Trash2, Briefcase, ChevronDown, CalendarClock, MessageSquare, Search, X, Upload } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { EnquiryImportModal } from '@/components/training/EnquiryImportModal';
import AuthContext from '@/contexts/AuthContext';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatApiError } from '@/utils/apiError';
import { LeadFields, NotesLog, BookOntoCourseButton, BookOntoCourseDialog, SmsLeadButton, callbackTone } from '@/components/training/EnquiryLeadTools';

const STATUSES = [
  { key: 'new',       label: 'New',       color: '#D4A24B', ink: '#0B0B0E' },
  { key: 'contacted', label: 'Contacted', color: '#2563eb', ink: '#fff' },
  { key: 'hot',       label: 'Hot',       color: '#ea580c', ink: '#fff' },
  { key: 'booked',    label: 'Booked',    color: '#059669', ink: '#fff' },
  { key: 'lost',      label: 'Lost',      color: '#dc2626', ink: '#fff' },
  { key: 'archived',  label: 'Archived',  color: '#6b7280', ink: '#fff' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map(s => [s.key, s]));

const BRAND_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
const INTEREST_COLOR = { probound: '#2F5E96', proepoxy: '#6B2FA0', prodecor: '#B8860B' };
const HEAR_ABOUT_LABEL = {
  google: 'Google',
  meta: 'Meta (FB / IG)',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  friend: 'Friend / colleague',
  trade_show: 'Trade show',
  other: 'Other',
};

export default function StaffTrainingEnquiries() {
  const navigate = useNavigate();
  const { isStaff, user } = useContext(AuthContext);
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  // Deep link from the staff alert email: show everything so the row is
  // visible regardless of status, then scroll to + highlight it.
  const [tab, setTab] = useState(focusId ? 'all' : 'new');  // 'all' | one of STATUSES.key
  useEffect(() => {
    if (!focusId || loading) return;
    const el = document.querySelector(`[data-testid="training-row-${focusId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusId, loading]);

  const [showImport, setShowImport] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-enquiries`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load');
      setRows(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isStaff) { navigate('/'); return; }
    load();
  }, [isStaff, navigate, load]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    for (const s of STATUSES) c[s.key] = 0;
    for (const r of rows) {
      c[r.status] = (c[r.status] || 0) + 1;
    }
    return c;
  }, [rows]);

  const [query, setQuery] = useState('');
  const [interest, setInterest] = useState(null); // null | 'probound' | 'proepoxy' | 'prodecor'
  const [todayOnly, setTodayOnly] = useState(false); // "Today's calls" — call-backs due today or overdue
  const dueToday = (r) => ['today', 'overdue'].includes(callbackTone(r.callback_date, r.status));
  const todayCount = useMemo(() => rows.filter(dueToday).length, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const q = query.trim().toLowerCase();
  const matchesInterest = (r) => !interest || (r.interested_courses || []).includes(interest) || (r.kind === 'course' && r.brand === interest);
  const qCompact = q.replace(/\s+/g, '');

  const visible = useMemo(() => {
    // Searching looks across every status so a lead is never "hidden" by the active tab.
    const list = q
      ? rows.filter(r => {
          const name = `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase();
          const compact = (v) => (v || '').toLowerCase().replace(/\s+/g, '');
          return name.includes(q) || (r.email || '').toLowerCase().includes(q)
            || compact(r.phone).includes(qCompact) || compact(r.postcode).includes(qCompact) || (r.id || '').toLowerCase() === q;
        })
      : todayOnly
        ? rows.filter(dueToday).filter(matchesInterest)
        : (tab === 'all' ? rows : rows.filter(r => r.status === tab)).filter(matchesInterest);
    // Leads with a call-back arranged sit at the top in date + time order (soonest first);
    // everything else follows, newest first.
    const cbKey = (r) => (callbackTone(r.callback_date, r.status) ? `${r.callback_date}T${r.callback_time || '99:99'}` : null);
    return [...list].sort((a, b) => {
      const ka = cbKey(a), kb = cbKey(b);
      if (ka && kb) return ka.localeCompare(kb);
      if (ka || kb) return ka ? -1 : 1;
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
  }, [rows, tab, q, qCompact, interest, todayOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = async (id, patch) => {
    try {
      const res = await fetch(`${API_URL}/api/staff/training-enquiries/${id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      const updated = await res.json();
      setRows(prev => prev.map(r => r.id === id ? updated : r));
      toast.success('Saved.');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const replaceRow = (updated) => setRows(prev => prev.map(r => r.id === updated.id ? updated : r));

  const deleteRow = async (row) => {
    const ok = await confirm({
      title: 'Delete this enquiry?',
      body: `Permanently remove the enquiry from ${row.first_name} ${row.last_name}? Use "Archived" if you just want to hide it.`,
      confirmLabel: 'Delete', tone: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/training-enquiries/${row.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      setRows(prev => prev.filter(r => r.id !== row.id));
      toast.success('Deleted.');
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} data-testid="training-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Staff Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Training Enquiries
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Incoming from the website embed form · auto-emailed to training@oxresin.co.uk</p>
          </div>
          <Button onClick={() => setShowImport(true)} size="sm" className="bg-[#D4A24B] hover:bg-[#c2933f] text-[#0B0B0E] font-bold shrink-0 px-2 sm:px-3" data-testid="enq-import-open">
            <Upload className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Import from spreadsheet</span>
          </Button>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-5 space-y-4">
        <div className="relative max-w-xl">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, phone, email or postcode…"
            className="pl-9 pr-9 h-10 bg-white" data-testid="training-search"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100" aria-label="Clear search" data-testid="training-search-clear">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className={`flex flex-wrap items-center gap-2 ${q ? 'opacity-40 pointer-events-none' : ''}`} data-testid="training-interest-filter">
          <span className="text-xs font-semibold text-gray-500">Interested in</span>
          {[['probound', 'ProBound'], ['proepoxy', 'ProEpoxy'], ['prodecor', 'ProDecor']].map(([k, l]) => {
            const on = interest === k;
            const n = rows.filter((r) => (tab === 'all' || r.status === tab) && ((r.interested_courses || []).includes(k) || (r.kind === 'course' && r.brand === k))).length;
            return (
              <button key={k} type="button" onClick={() => setInterest(on ? null : k)} aria-pressed={on} data-testid={`training-interest-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${on ? 'text-white border-transparent shadow' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}
                style={on ? { backgroundColor: INTEREST_COLOR[k] } : undefined}>
                {l}<span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${on ? 'bg-white/25' : 'bg-gray-100 text-gray-600'}`}>{n}</span>
              </button>
            );
          })}
          {interest && <button type="button" onClick={() => setInterest(null)} className="text-xs text-gray-500 hover:text-gray-900 underline" data-testid="training-interest-clear">Show all</button>}
        </div>
        <div className={`flex flex-wrap items-center gap-2 ${q ? 'opacity-40 pointer-events-none' : ''}`} data-testid="training-tabs">
          <button type="button" onClick={() => setTodayOnly(v => !v)} aria-pressed={todayOnly} data-testid="tab-today"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold border-2 transition-colors ${todayOnly ? 'bg-amber-500 border-amber-500 text-white shadow' : 'bg-white text-amber-800 border-amber-300 hover:border-amber-400'}`}
            title="Call-backs due today (and any overdue)">
            <PhoneCall className="w-3.5 h-3.5" /> Today's calls
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${todayOnly ? 'bg-white/25' : todayCount ? 'bg-amber-100 text-amber-900' : 'bg-gray-100 text-gray-600'}`} data-testid="tab-today-count">{todayCount}</span>
          </button>
          <span className="w-px h-6 bg-gray-200 mx-1" aria-hidden />
          <div className={`flex flex-wrap gap-2 ${todayOnly ? 'opacity-40 pointer-events-none' : ''}`}>
            {STATUSES.map(s => (
              <TabBtn key={s.key} active={tab === s.key} onClick={() => setTab(s.key)} label={s.label} count={counts[s.key]} colour={s.color} testid={`tab-${s.key}`} />
            ))}
            <TabBtn active={tab === 'all'} onClick={() => setTab('all')} label="All" count={counts.all} testid="tab-all" />
          </div>
        </div>

        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : visible.length === 0 ? (
          <Card className="p-10 text-center text-gray-500 italic" data-testid="training-empty">
            {q ? <>No enquiries match “{query.trim()}”.</> : todayOnly ? 'No call-backs due today — all caught up.' : interest ? `No ${tab === 'all' ? '' : tab + ' '}leads interested in ${BRAND_LABEL[interest]}.` : tab === 'new' ? 'No new training enquiries — nice work.' : `No ${tab === 'all' ? '' : tab + ' '}enquiries.`}
          </Card>
        ) : (
          <div className="space-y-1.5" data-testid="training-list">
            {q && <p className="text-xs text-gray-500 px-1" data-testid="training-search-summary">{visible.length} match{visible.length === 1 ? '' : 'es'} across all statuses</p>}
            {visible.map(row => (
              <EnquiryRow key={row.id} row={row} focused={row.id === focusId} agentName={(user?.name || '').split(' ')[0]} onUpdate={updateRow} onChanged={replaceRow} onDelete={() => deleteRow(row)} />
            ))}
          </div>
        )}

      </div>
      {showImport && <EnquiryImportModal onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); load(); }} />}
    </div>
  );
}

// ─── sub-components ─────────────────────────────────────────────────────────

function TabBtn({ active, onClick, label, count, colour, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-2 rounded-lg text-sm font-bold border-2 transition-colors ${active ? 'text-white border-transparent shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}
      style={active ? { backgroundColor: colour || '#0B0B0E' } : undefined}
    >
      {label}
      <span className={`ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
    </button>
  );
}

function EnquiryRow({ row, focused, agentName, onUpdate, onChanged, onDelete }) {
  const [bookOpen, setBookOpen] = useState(false);
  const [expanded, setExpanded] = useState(focused);
  const stat = STATUS_LABEL[row.status] || STATUS_LABEL.new;
  const leadIn = new Date(row.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const hearLabel = HEAR_ABOUT_LABEL[row.hear_about] || row.hear_about;
  const canBook = row.status !== 'booked';
  const tone = callbackTone(row.callback_date, row.status);
  const noteCount = (row.notes_log || []).length + (row.notes && row.notes.trim() && !(row.notes_log || []).length ? 1 : 0);
  const edge = row.status === 'booked' ? 'border-l-emerald-500' : tone === 'overdue' ? 'border-l-rose-400' : tone === 'today' ? 'border-l-amber-400' : 'border-l-transparent';

  const onStatusPick = (value) => {
    if (value === 'booked') { setBookOpen(true); return; }
    onUpdate(row.id, { status: value });
  };
  const stop = (e) => e.stopPropagation();

  return (
    <Card data-testid={`training-row-${row.id}`} className={`overflow-hidden border-l-4 ${edge} ${focused ? 'ring-2 ring-[#D4A24B] shadow-lg' : ''}`}>
      <BookOntoCourseDialog row={row} open={bookOpen} onOpenChange={setBookOpen} onChanged={onChanged} />

      <div
        role="button" tabIndex={0} onClick={() => setExpanded(v => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v); } }}
        className="px-3 sm:px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 cursor-pointer hover:bg-gray-50/80 transition-colors"
        data-testid={`training-row-toggle-${row.id}`}
      >
        <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate max-w-[220px]">{row.first_name} {row.last_name}</h3>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold shrink-0" style={{ backgroundColor: stat.color, color: stat.ink }} data-testid={`training-row-status-${row.id}`}>{stat.label}</span>
        {row.kind === 'course' ? (
          <span className="hidden md:inline text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold bg-purple-100 text-purple-800 shrink-0" data-testid={`training-row-kind-${row.id}`}>{BRAND_LABEL[row.brand] || 'Course'}</span>
        ) : (
          <span className="hidden md:inline text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold bg-gray-100 text-gray-600 shrink-0" data-testid={`training-row-kind-${row.id}`}>General</span>
        )}

        <div className="hidden lg:flex items-center gap-x-3 text-xs text-gray-600 min-w-0">
          <a href={`tel:${row.phone}`} onClick={stop} className="inline-flex items-center gap-1 hover:text-emerald-700 whitespace-nowrap"><Phone className="w-3 h-3" /> {row.phone}</a>
          <a href={`mailto:${row.email}`} onClick={stop} className="inline-flex items-center gap-1 hover:text-emerald-700 truncate max-w-[240px]"><Mail className="w-3 h-3 shrink-0" /> <span className="truncate">{row.email}</span></a>
          <span className="text-gray-400 whitespace-nowrap" title={row.created_at}>Lead in {leadIn}</span>
          {(row.interested_courses || []).map((c) => (
            <span key={c} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold whitespace-nowrap" style={{ backgroundColor: INTEREST_COLOR[c] || '#e5e7eb', color: '#fff' }} data-testid={`training-row-interest-${row.id}-${c}`} title="Course they're interested in">{BRAND_LABEL[c] || c}</span>
          ))}
          {row.callback_date && tone && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${tone === 'overdue' ? 'bg-rose-50 text-rose-700' : tone === 'today' ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-700'}`} data-testid={`training-row-callback-${row.id}`}>
              <CalendarClock className="w-3 h-3" /> {new Date(row.callback_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}{row.callback_time ? ` ${row.callback_time}` : ''}{tone === 'overdue' ? ' overdue' : tone === 'today' ? ' today' : ''}
            </span>
          )}
          {row.chance_percent != null && row.status !== 'booked' && <span className="text-gray-500 whitespace-nowrap">{row.chance_percent}%</span>}
          {noteCount > 0 && <span className="inline-flex items-center gap-1 text-gray-500 whitespace-nowrap" data-testid={`training-row-notes-${row.id}`}><MessageSquare className="w-3 h-3" /> {noteCount}</span>}
          {row.booked_course_label && <span className="text-emerald-700 font-semibold truncate max-w-[220px]" data-testid={`booked-label-${row.id}`}>{row.booked_course_label}{row.booking_guests?.length ? ` · +${row.booking_guests.length}` : ''}</span>}
        </div>

        <div className="flex flex-wrap items-center gap-2 ml-auto w-full sm:w-auto sm:shrink-0" onClick={stop} onKeyDown={stop}>
          {row.phone && (
            <a href={`tel:${(row.phone || '').replace(/[^\d+]/g, '')}`} data-testid={`call-lead-${row.id}`} title={`Call ${row.phone}`}
              className={`inline-flex items-center h-8 px-2.5 rounded-lg border-2 text-xs font-bold transition-colors ${tone === 'today' || tone === 'overdue' ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`}>
              <PhoneCall className="w-3.5 h-3.5 mr-1" /> Call
            </a>
          )}
          {canBook && <SmsLeadButton row={row} agentName={agentName} onChanged={onChanged} />}
          {canBook ? (
            <Button size="sm" onClick={() => setBookOpen(true)} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`book-training-${row.id}`}>
              <GraduationCap className="w-3.5 h-3.5 mr-1" /> Book training
            </Button>
          ) : (
            <BookOntoCourseButton row={row} onOpen={() => setBookOpen(true)} />
          )}
          <select
            value={row.status}
            onChange={(e) => onStatusPick(e.target.value)}
            data-testid={`training-status-${row.id}`}
            className="h-8 px-2 text-sm font-semibold border-2 border-gray-200 rounded-lg bg-white hover:border-gray-300 focus:outline-none focus:border-emerald-500"
          >
            {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100" data-testid={`training-row-detail-${row.id}`}>
          <div className="px-3 sm:px-4 py-3 space-y-2.5">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600 lg:hidden">
              <a href={`mailto:${row.email}`} className="inline-flex items-center gap-1 hover:text-emerald-700"><Mail className="w-3.5 h-3.5" /> {row.email}</a>
              <a href={`tel:${row.phone}`} className="inline-flex items-center gap-1 hover:text-emerald-700"><Phone className="w-3.5 h-3.5" /> {row.phone}</a>
            </div>
            {row.message && (
              <p className="text-xs sm:text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 whitespace-pre-wrap" data-testid={`training-row-message-${row.id}`}>{row.message}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
              <span>Heard via <strong className="text-gray-700">{hearLabel}</strong>{row.hear_about === 'other' && row.hear_about_other && <> — “{row.hear_about_other}”</>}</span>
              {row.occupation && <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {row.occupation}</span>}
              {(row.interested_courses || []).length > 0 && <span>Interested in <strong className="text-gray-700">{row.interested_courses.map((c) => BRAND_LABEL[c] || c).join(', ')}</strong></span>}
              {row.postcode && <span>{row.postcode}</span>}
              <span title={row.created_at}>Lead in {new Date(row.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span>
              <span className="text-gray-400 font-mono">#{row.id}</span>
              {row.kind === 'course' && <span className="text-purple-700 font-semibold">{BRAND_LABEL[row.brand] || 'Course'} course form</span>}
              {row.booked_course_label && <span className="text-emerald-700 font-semibold">Booked: {row.booked_course_label}</span>}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <LeadFields row={row} onUpdate={onUpdate} />
              <Button variant="outline" size="sm" onClick={onDelete} data-testid={`training-delete-${row.id}`} className="h-8 text-rose-700 border-rose-200 hover:bg-rose-50">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            </div>
          </div>
          <NotesLog row={row} onChanged={onChanged} />
        </div>
      )}
    </Card>
  );
}
